// src/features/photoface/components/PhotoFaceTab.jsx
// تبويب "ألبوم الوجوه" داخل صفحة تفاصيل الحجز — إدارة ألبومات PhotoFace
// الرفع يتم من المتصفح إلى S3 مباشرة عبر presigned URLs (لا يمر عبر السيرفر)

import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchPhotoFaceAlbums,
  createPhotoFaceAlbum,
  deletePhotoFaceAlbum,
  fetchLiveStatus,
  fetchAlbumPhotos,
  requestUploadUrls,
  confirmUpload,
  generateLink,
  revokeLink,
  fetchCredits,
  proxyUploadToS3,
  setReviewMode,
  deleteAlbumPhoto,
  sharePhoto,
  updatePhotoNote,
  fetchReviewRequests,
  approveReviewRequest,
  rejectReviewRequest,
} from '../../../api/photoface.api';

import styles from './PhotoFace.module.css';

const ALLOWED_EXT = /\.(jpe?g|png)$/i;
const COMPRESS_OVER = 20 * 1024 * 1024; // فوقها لا يفهرس أمازون الوجوه — نضغط قبل الرفع

/** ضغط صورة كبيرة داخل المتصفح (4000px / جودة 88%) — يعيد null عند الفشل */
const compressLargeFile = async (file) => {
  try {
    const bmp = await createImageBitmap(file);
    const maxSide = 4000;
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.88));
    if (!blob) return null;
    return new File([blob], file.name.replace(/\.png$/i, '.jpg'), { type: 'image/jpeg' });
  } catch {
    return null;
  }
};

/** رفع مباشر إلى S3 من المتصفح (نفس طريقة فوتوفيس) — يرمي خطأ إن منعته سياسة الحاوية */
const directPutToS3 = async (uploadUrl, file, contentType) => {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || 'image/jpeg' },
    body: file,
  });
  if (!res.ok) throw new Error(`S3 PUT ${res.status}`);
};

const fmtNum = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const errMsg = (err, fallback) =>
  err?.response?.data?.error || err?.message || fallback;

// ════════════════════════════════════════════════════════════
// شريط رصيد حساب الخدمة
// ════════════════════════════════════════════════════════════
const CreditsStrip = ({ refreshSignal }) => {
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchCredits()
      .then((res) => { if (mounted) setCredits(res.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [refreshSignal]);

  if (!credits) return null;
  return (
    <div className={styles.creditsStrip}>
      <span className={styles.creditChip}>
        📊 الرصيد المتبقي: <b>{fmtNum(credits.remaining_photos)}</b> صورة
      </span>
      <span className={styles.creditChip}>
        🖼️ المعالَج: <b>{fmtNum(credits.photos_processed_count)}</b>
      </span>
      <span className={styles.creditChip}>
        💾 المساحة: <b>{fmtNum(credits.current_storage_used_mb)}</b> م.ب
        مستخدمة من {fmtNum(credits.storage_limit_mb)}
      </span>
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// بطاقة طلب مراجعة واحد (النمط الآمن)
// ════════════════════════════════════════════════════════════
const REQ_STATUS_LABELS = {
  PENDING:   '⏳ جاري تحليل السيلفي…',
  READY:     '📥 بانتظار موافقتك',
  COMPLETED: '✅ تمت الموافقة',
  REJECTED:  '⛔ مرفوض',
};

const RequestCard = ({ albumId, req, onChanged }) => {
  // الصور بثقة 80%+ مختارة مسبقاً لتسريع عمل المدير
  const [selected, setSelected] = useState(() => new Set(
    (req.aws_analysis_results || [])
      .filter((c) => c.confidence >= 80)
      .map((c) => c.photo_id),
  ));
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const toggle = (photoId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
      return next;
    });
  };

  const handleApprove = async () => {
    if (!selected.size) { setError('اختر صورة واحدة على الأقل قبل الموافقة.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await approveReviewRequest(albumId, req.id, [...selected]);
      if (data.share_link) {
        try { await navigator.clipboard?.writeText(data.share_link); } catch { /* تجاهل */ }
      }
      alert(`تمت الموافقة ✅\nرابط صور الضيف (تم نسخه):\n${data.share_link ?? ''}`);
      onChanged();
    } catch (err) {
      setError(errMsg(err, 'تعذرت الموافقة على الطلب.'));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('رفض الطلب نهائياً؟ سيرى الضيف أن طلبه رُفض.')) return;
    setBusy(true);
    try {
      await rejectReviewRequest(albumId, req.id);
      onChanged();
    } catch (err) {
      setError(errMsg(err, 'تعذر رفض الطلب.'));
    } finally {
      setBusy(false);
    }
  };

  const selfies    = req.selfie_urls || {};
  const candidates = req.aws_analysis_results || [];

  return (
    <div className={styles.requestCard}>
      <div className={styles.requestHeader}>
        <span>طلب رقم {req.id} — {String(req.created_at ?? '').slice(0, 16).replace('T', ' ')}</span>
        <span className={`${styles.statusBadge} ${styles[`reqStatus${req.status}`] ?? ''}`}>
          {REQ_STATUS_LABELS[req.status] ?? req.status}
        </span>
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {(selfies.front || selfies.right || selfies.left) && (
        <div>
          <div className={styles.hint}>سيلفي الضيف:</div>
          <div className={styles.selfiesRow}>
            {['front', 'right', 'left'].map((k) => selfies[k] && (
              <img key={k} src={selfies[k]} alt="" className={styles.selfieThumb} />
            ))}
          </div>
        </div>
      )}

      {req.status === 'PENDING' && (
        <div className={styles.hint}>التحليل يعمل في الخلفية — اضغط "تحديث الطلبات" بعد لحظات.</div>
      )}

      {candidates.length > 0 && req.status !== 'REJECTED' && (
        <div>
          <div className={styles.hint}>
            الصور المرشحة مرتبة حسب نسبة التطابق —
            {req.status === 'READY' ? ' اضغط على الصورة لاختيار ما سيظهر للضيف:' : ' المعتمدة سابقاً:'}
          </div>
          <div className={styles.candidatesGrid}>
            {candidates.map((c) => (
              <div
                key={c.photo_id}
                className={`${styles.candidateCell} ${selected.has(c.photo_id) ? styles.candidateSelected : ''}`}
                onClick={() => req.status === 'READY' && toggle(c.photo_id)}
              >
                <img src={c.url} alt="" className={styles.candidateImg} loading="lazy" />
                <span className={styles.candidateConf}>{c.confidence}%</span>
                {selected.has(c.photo_id) && <span className={styles.candidateCheck}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {req.status === 'READY' && candidates.length === 0 && (
        <div className={styles.hint}>لم يجد التحليل صوراً مرشحة — يمكنك رفض الطلب.</div>
      )}

      {req.status === 'READY' && (
        <div className={styles.actionsRow}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy || !selected.size}
            onClick={handleApprove}
          >
            ✅ موافقة وإرسال ({selected.size})
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            disabled={busy}
            onClick={handleReject}
          >
            ⛔ رفض الطلب
          </button>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// بطاقة ألبوم واحد
// ════════════════════════════════════════════════════════════
const AlbumCard = ({ album, onDeleted, onCreditsChanged }) => {
  const [live, setLive]         = useState(null);
  const [photos, setPhotos]     = useState([]);
  const [photosPage, setPhotosPage] = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);

  const [uploading, setUploading]   = useState(false);
  const [uploadDone, setUploadDone] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);

  const [linkInfo, setLinkInfo]     = useState({
    link: album.share_link,
    qr: album.qr_code_url,
    expires: album.link_expires_at,
  });
  const [hours, setHours]       = useState('24');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  const [reviewBusy, setReviewBusy]       = useState(false);
  const [showRequests, setShowRequests]   = useState(false);
  const [requests, setRequests]           = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const fileInputRef = useRef(null);
  const pollRef      = useRef(null);

  // ── الحالة الحية + polling أثناء المعالجة ──
  const loadLive = useCallback(async () => {
    try {
      const res = await fetchLiveStatus(album.id);
      setLive(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [album.id]);

  useEffect(() => {
    loadLive();
    return () => clearInterval(pollRef.current);
  }, [loadLive]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await loadLive();
      if (data && !data.is_processing) {
        clearInterval(pollRef.current);
        onCreditsChanged();
      }
    }, 6000);
  }, [loadLive, onCreditsChanged]);

  // ── الصور ──
  const loadPhotos = useCallback(async (page) => {
    try {
      const res = await fetchAlbumPhotos(album.id, page);
      const data = res.data;
      const results = data.results ?? data;
      setPhotos((prev) => (page === 1 ? results : [...prev, ...results]));
      setPhotosPage(page);
      setHasMore(Boolean(data.next));
    } catch (err) {
      setError(errMsg(err, 'تعذر جلب الصور.'));
    }
  }, [album.id]);

  const togglePhotos = () => {
    if (!showPhotos && photosPage === 0) loadPhotos(1);
    setShowPhotos((v) => !v);
  };

  // ── أدوات الصورة المفردة ──
  const handleSharePhoto = async (photoId) => {
    try {
      const { data } = await sharePhoto(album.id, photoId);
      try { await navigator.clipboard?.writeText(data.url); } catch { /* تجاهل */ }
      alert('تم نسخ رابط مشاركة مباشر للصورة (صالح 24 ساعة).');
    } catch (err) {
      setError(errMsg(err, 'تعذر توليد رابط المشاركة.'));
    }
  };

  const handlePhotoNote = async (photo) => {
    const note = window.prompt('ملاحظة الصورة:', photo.note || '');
    if (note === null) return;
    try {
      await updatePhotoNote(album.id, photo.id, note);
      setPhotos((prev) => prev.map((x) => (x.id === photo.id ? { ...x, note } : x)));
    } catch (err) {
      setError(errMsg(err, 'تعذر حفظ الملاحظة.'));
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('حذف هذه الصورة نهائياً من الألبوم؟')) return;
    try {
      await deleteAlbumPhoto(album.id, photoId);
      setPhotos((prev) => prev.filter((x) => x.id !== photoId));
      loadLive();
      onCreditsChanged();
    } catch (err) {
      setError(errMsg(err, 'تعذر حذف الصورة.'));
    }
  };

  // ── النمط الآمن + طلبات المراجعة ──
  const handleToggleReview = async () => {
    setReviewBusy(true);
    setError('');
    try {
      await setReviewMode(album.id, !live?.review_mode);
      await loadLive();
    } catch (err) {
      setError(errMsg(err, 'تعذر تغيير النمط الآمن.'));
    } finally {
      setReviewBusy(false);
    }
  };

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const { data } = await fetchReviewRequests(album.id);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(errMsg(err, 'تعذر جلب طلبات المراجعة.'));
    } finally {
      setRequestsLoading(false);
    }
  }, [album.id]);

  const toggleRequests = () => {
    if (!showRequests) loadRequests();
    setShowRequests((v) => !v);
  };

  // ── الرفع ──
  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;

    const invalid = files.filter((f) => !ALLOWED_EXT.test(f.name));
    if (invalid.length) {
      setError(`الملفات المسموحة JPG و PNG فقط — تم رفض: ${invalid.map((f) => f.name).join('، ')}`);
      return;
    }

    // فوق 80 م.ب مرفوض؛ 20-80 م.ب تُضغط تلقائياً (في المتصفح، أو في السيرفر احتياطاً)
    const HARD_MAX_MB = 80;
    const tooBig = files.filter((f) => f.size > HARD_MAX_MB * 1024 * 1024);
    if (tooBig.length) {
      setError(
        `ملفات أكبر من ${HARD_MAX_MB} م.ب مرفوضة: ` +
        `${tooBig.slice(0, 5).map((f) => f.name).join('، ')}${tooBig.length > 5 ? '…' : ''}`
      );
      return;
    }

    setError('');
    setUploading(true);
    setUploadTotal(files.length);
    setUploadDone(0);

    try {
      // 1) ضغط الكبيرة في المتصفح قبل أي شيء (حتى تكون الميتاداتا صحيحة)
      const processed = [];
      for (let i = 0; i < files.length; i += 1) {
        let f = files[i];
        if (f.size > COMPRESS_OVER) {
          // eslint-disable-next-line no-await-in-loop
          const small = await compressLargeFile(f);
          if (small) f = small; // فشل الضغط؟ يبقى الأصل ويتكفل السيرفر به
        }
        processed.push(f);
      }

      const meta = processed.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type || 'image/jpeg',
      }));
      const { data: urls } = await requestUploadUrls(album.id, meta);

      const s3Keys = [];
      for (let i = 0; i < urls.length; i += 1) {
        const item = urls[i];
        const file = processed[i];
        // الرفع المباشر إلى S3 (طريقة فوتوفيس) — وإن منعته سياسة الحاوية
        // أو بقي الملف كبيراً، نسقط تلقائياً على مسار السيرفر (يضغط ويرفع بالنيابة)
        if (file.size <= COMPRESS_OVER) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await directPutToS3(item.upload_url, file, item.content_type);
          } catch {
            // eslint-disable-next-line no-await-in-loop
            await proxyUploadToS3(album.id, file, item.upload_url, item.content_type);
          }
        } else {
          // eslint-disable-next-line no-await-in-loop
          await proxyUploadToS3(album.id, file, item.upload_url, item.content_type);
        }
        s3Keys.push(item.s3_key);
        setUploadDone(i + 1);
      }

      await confirmUpload(album.id, s3Keys);
      await loadLive();
      startPolling();
      if (showPhotos) loadPhotos(1);
    } catch (err) {
      setError(errMsg(err, 'فشل رفع الصور، حاول مرة أخرى.'));
    } finally {
      setUploading(false);
    }
  };

  // ── الرابط والـ QR (مدة حرة بالساعات) ──
  const handleGenerateLink = async () => {
    const h = parseInt(hours, 10);
    if (!h || h < 1) { setError('أدخل مدة صحيحة بالساعات.'); return; }
    if (linkInfo.link && !window.confirm(
      'توليد رابط جديد سيلغي الرابط والـ QR الحاليين فوراً. هل تريد المتابعة؟'
    )) return;

    setBusy(true);
    setError('');
    try {
      const { data } = await generateLink(album.id, h);
      setLinkInfo({ link: data.link, qr: data.qr_code, expires: data.expires_at });
      loadLive();
    } catch (err) {
      setError(errMsg(err, 'تعذر توليد الرابط.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('سيتوقف رابط الضيوف عن العمل فوراً. متأكد؟')) return;
    setBusy(true);
    try {
      await revokeLink(album.id);
      setLinkInfo({ link: '', qr: '', expires: null });
      loadLive();
    } catch (err) {
      setError(errMsg(err, 'تعذر إلغاء الرابط.'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`حذف ألبوم "${album.name}" وكل صوره نهائياً من PhotoFace؟`)) return;
    setBusy(true);
    try {
      await deletePhotoFaceAlbum(album.id);
      onDeleted(album.id);
    } catch (err) {
      setError(errMsg(err, 'تعذر حذف الألبوم.'));
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(linkInfo.link)
      .then(() => alert('تم نسخ الرابط.'))
      .catch(() => {});
  };

  // ── حالة الشارة ──
  const expired = live?.expired === true;
  let badge = <span className={styles.statusBadge}>بلا صور بعد</span>;
  if (expired) {
    badge = <span className={`${styles.statusBadge} ${styles.statusExpired}`}>🗑 انتهت مدة الاحتفاظ — حُذفت الصور</span>;
  } else if (live?.is_processing) {
    badge = <span className={`${styles.statusBadge} ${styles.statusProcessing}`}>⏳ جاري فهرسة الوجوه…</span>;
  } else if (live?.photos_count > 0) {
    badge = <span className={`${styles.statusBadge} ${styles.statusReady}`}>✅ جاهز — {live.photos_count} صورة</span>;
  }

  // انتهاء مدة الاحتفاظ: حُذفت الصور تلقائياً — بطاقة مبسّطة بزر إزالة فقط
  if (expired) {
    return (
      <div className={styles.albumCard}>
        <div className={styles.albumHeader}>
          <div>
            <div className={styles.albumName}>📸 {album.name}</div>
            <div className={styles.albumMeta}>
              أُنشئ في {album.created_at ? album.created_at.slice(0, 10) : ''}
            </div>
          </div>
          {badge}
        </div>
        {error && <div className={styles.errorMsg}>{error}</div>}
        <div className={styles.hint}>
          {live?.message || 'انتهت مدة الاحتفاظ وحُذفت صور هذا الألبوم من التخزين السحابي.'}
          {' '}يمكنك إزالة هذه البطاقة أو إنشاء ألبوم جديد للحجز.
        </div>
        <div className={styles.actionsRow}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            disabled={busy}
            onClick={handleDelete}
          >
            إزالة البطاقة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.albumCard}>

      <div className={styles.albumHeader}>
        <div>
          <div className={styles.albumName}>📸 {album.name}</div>
          <div className={styles.albumMeta}>
            أُنشئ في {album.created_at ? album.created_at.slice(0, 10) : ''}
          </div>
        </div>
        {badge}
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {/* أزرار العمليات */}
      <div className={styles.actionsRow}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          multiple
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={uploading || live?.is_processing}
          onClick={() => fileInputRef.current?.click()}
        >
          ⬆ رفع صور
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={togglePhotos}
        >
          🖼 {showPhotos ? 'إخفاء الصور' : 'عرض الصور'}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnDanger}`}
          disabled={busy || uploading}
          onClick={handleDelete}
        >
          حذف الألبوم
        </button>
      </div>

      {/* تقدم الرفع */}
      {uploading && (
        <div className={styles.uploadProgress}>
          جاري رفع الصور إلى التخزين السحابي… {uploadDone} / {uploadTotal}
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${uploadTotal ? (uploadDone / uploadTotal) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* النمط الآمن (المراجعة اليدوية) */}
      <div className={styles.reviewToggleRow}>
        <button
          type="button"
          className={`${styles.toggleSwitch} ${live?.review_mode ? styles.toggleOn : ''}`}
          disabled={reviewBusy}
          onClick={handleToggleReview}
          aria-label="تبديل النمط الآمن"
        />
        <b>🛡️ النمط الآمن</b>
        <span className={styles.hint}>
          {live?.review_mode
            ? 'مفعّل — طلبات الضيوف تنتظر موافقتك قبل ظهور الصور.'
            : 'متوقف — الضيف يرى صوره فوراً بعد السيلفي.'}
        </span>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={toggleRequests}
        >
          📥 {showRequests ? 'إخفاء الطلبات' : 'طلبات المراجعة'}
        </button>
      </div>

      {/* طلبات المراجعة اليدوية */}
      {showRequests && (
        <>
          {requestsLoading && <div className={styles.hint}>جاري جلب الطلبات…</div>}
          {!requestsLoading && requests.length === 0 && (
            <div className={styles.hint}>لا توجد طلبات مراجعة لهذا الألبوم بعد.</div>
          )}
          {requests.map((r) => (
            <RequestCard key={r.id} albumId={album.id} req={r} onChanged={loadRequests} />
          ))}
          {!requestsLoading && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary} ${styles.loadMore}`}
              onClick={loadRequests}
            >
              ⟳ تحديث الطلبات
            </button>
          )}
        </>
      )}

      {/* شبكة الصور */}
      {showPhotos && (
        <>
          <div className={styles.photosGrid}>
            {photos.map((p) => (
              <div key={p.id} className={styles.photoCell}>
                <img src={p.url} alt="" className={styles.photoThumb} loading="lazy" />
                <div className={styles.photoTools}>
                  <button
                    type="button" className={styles.toolBtn} title="رابط مشاركة 24 ساعة"
                    onClick={() => handleSharePhoto(p.id)}
                  >🔗</button>
                  <button
                    type="button" className={styles.toolBtn} title="ملاحظة"
                    onClick={() => handlePhotoNote(p)}
                  >✏️</button>
                  <button
                    type="button" className={styles.toolBtn} title="حذف الصورة"
                    onClick={() => handleDeletePhoto(p.id)}
                  >🗑</button>
                </div>
                {p.note && <div className={styles.photoNote} title={p.note}>📝 {p.note}</div>}
              </div>
            ))}
          </div>
          {photos.length === 0 && <div className={styles.hint}>لا توجد صور في الألبوم بعد.</div>}
          {hasMore && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary} ${styles.loadMore}`}
              onClick={() => loadPhotos(photosPage + 1)}
            >
              تحميل المزيد
            </button>
          )}
        </>
      )}

      {/* قسم رابط الضيوف */}
      <div className={styles.linkBox}>
        {linkInfo.qr && <img src={linkInfo.qr} alt="QR" className={styles.qrImage} />}
        <div className={styles.linkDetails}>
          {linkInfo.link ? (
            <>
              <div className={styles.linkUrl}>{linkInfo.link}</div>
              {linkInfo.expires && (
                <div className={styles.linkExpiry}>
                  صالح حتى: {String(linkInfo.expires).replace('T', ' ').slice(0, 16)}
                </div>
              )}
              <div className={styles.actionsRow}>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={copyLink}>
                  نسخ الرابط
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDanger}`}
                  disabled={busy}
                  onClick={handleRevoke}
                >
                  إلغاء الرابط
                </button>
              </div>
            </>
          ) : (
            <div className={styles.hint}>
              لا يوجد رابط فعال — ولّد رابطاً ليتمكن الضيوف من البحث عن صورهم بالسيلفي.
            </div>
          )}

          <div className={styles.durationRow}>
            <span className={styles.durationLabel}>مدة الصلاحية:</span>
            <input
              type="number"
              min="1"
              max="8760"
              className={styles.durationInput}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
            <span className={styles.durationLabel}>ساعة</span>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={busy}
              onClick={handleGenerateLink}
            >
              {linkInfo.link ? 'تحديث الرابط والمدة' : '🔗 توليد رابط + QR'}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

// ════════════════════════════════════════════════════════════
// التبويب الرئيسي
// ════════════════════════════════════════════════════════════
const PhotoFaceTab = ({ booking }) => {
  const [albums, setAlbums]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError]     = useState('');
  const [creditsSignal, setCreditsSignal] = useState(0);

  const bumpCredits = useCallback(() => setCreditsSignal((v) => v + 1), []);

  useEffect(() => {
    let mounted = true;
    fetchPhotoFaceAlbums(booking.id)
      .then((res) => {
        if (!mounted) return;
        const data = res.data;
        setAlbums(data.results ?? data);
      })
      .catch((err) => setError(errMsg(err, 'تعذر جلب الألبومات.')))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [booking.id]);

  const handleCreate = async () => {
    const name = newName.trim() || `${booking.client?.name ?? 'مناسبة'} — ${booking.booking_date ?? ''}`.trim();
    setCreating(true);
    setError('');
    try {
      const { data } = await createPhotoFaceAlbum(booking.id, name);
      setAlbums((prev) => [data, ...prev]);
      setNewName('');
    } catch (err) {
      setError(errMsg(err, 'تعذر إنشاء الألبوم.'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className={styles.wrapper}><div className={styles.hint}>جاري التحميل…</div></div>;

  return (
    <div className={styles.wrapper}>

      <CreditsStrip refreshSignal={creditsSignal} />

      {error && <div className={styles.errorMsg}>{error}</div>}

      {albums.map((album) => (
        <AlbumCard
          key={album.id}
          album={album}
          onDeleted={(id) => setAlbums((prev) => prev.filter((a) => a.id !== id))}
          onCreditsChanged={bumpCredits}
        />
      ))}

      {/* إنشاء ألبوم جديد */}
      <div className={styles.createBox}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
          {albums.length ? 'إضافة ألبوم آخر لهذا الحجز' : 'أنشئ ألبوم صور للمناسبة — بحث بالوجه للضيوف'}
        </div>
        <input
          className={styles.createInput}
          placeholder={`اسم الألبوم (افتراضياً: ${booking.client?.name ?? 'المناسبة'})`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={creating}
          onClick={handleCreate}
        >
          {creating ? 'جاري الإنشاء…' : '+ إنشاء ألبوم'}
        </button>
        <div className={styles.hint}>
          الصور تُرفع مباشرة إلى التخزين السحابي وتُفهرس الوجوه تلقائياً — ثم تولّد رابط QR للضيوف.
        </div>
      </div>

    </div>
  );
};

export default PhotoFaceTab;
